import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { ujmsDispositionConfig, ujmsDisposeCase, ujmsDispositionSheet } from "../../services/ujmsApi";
import { inputCls, selectCls, Field, btnPrimary, btnGhost } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Gavel, Lock } from "lucide-react";
import { DispositionSheet } from "../enterprise/charges/ChargeDispositions";

const CHARGE_TYPES = ["CRIMINAL", "TRAFFIC", "DOMESTIC_VIOLENCE"];

/** Case close. Criminal-type cases embed the charge-level disposition sheet: the case cannot close until every count
 *  carries a disposition and every guilty count carries a sentence (server-enforced, mirrored here). */
export function DisposeDialog({ open, onClose, caseId, caseType, caseNumber, onDone, hearingId }) {
  const ctx = useOutletContext();
  const access = ctx?.access;
  const [cfg, setCfg] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [f, setF] = useState({ disposition_type: "", method: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const chargeLevel = CHARGE_TYPES.includes(caseType);

  const loadSheet = () => (chargeLevel ? ujmsDispositionSheet(caseId).then((r) => setSheet(r.data)).catch(() => setSheet(null)) : Promise.resolve());
  useEffect(() => {
    if (!open) return;
    ujmsDispositionConfig().then((r) => setCfg(r.data)).catch(() => {});
    loadSheet();
  }, [open, caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const types = ["CRIMINAL", "TRAFFIC"].includes(caseType) ? cfg?.criminal : cfg?.default;
  const blocked = chargeLevel && sheet && sheet.total > 0 && !sheet.can_close;

  const submit = async () => {
    if (!f.disposition_type || !f.method) { toast.error("Disposition and method are required"); return; }
    setBusy(true);
    try {
      await ujmsDisposeCase(caseId, { ...f, charge_dispositions: [] });
      toast.success(`${caseNumber} disposed — docketed and certified`);
      setF({ disposition_type: "", method: "", notes: "" });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Disposition failed"); loadSheet(); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={chargeLevel ? "max-w-5xl" : "max-w-xl"} data-testid="dispose-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Gavel className="w-5 h-5" /> Dispose Case — {caseNumber}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          {chargeLevel && (
            <div className="space-y-1" data-testid="dispose-charge-sheet">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Step 1 — Dispose every count (charge level)</div>
              <DispositionSheet caseId={caseId} access={access} hearingId={hearingId} onChanged={loadSheet} />
            </div>
          )}
          <div className={blocked ? "opacity-60" : ""} data-testid="dispose-case-step">
            {chargeLevel && <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Step 2 — Close the case</div>}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Case disposition" required>
                <select className={selectCls} value={f.disposition_type} onChange={(e) => setF({ ...f, disposition_type: e.target.value })} disabled={blocked} data-testid="dispose-type-select">
                  <option value="">Select disposition…</option>
                  {(types || []).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Method" required>
                <select className={selectCls} value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} disabled={blocked} data-testid="dispose-method-select">
                  <option value="">Select method…</option>
                  {(cfg?.methods || []).map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Notes / judgment details">
              <textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} disabled={blocked} data-testid="dispose-notes" />
            </Field>
          </div>
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">
            Disposing enters a certified CASE_DISPOSED docket entry, vacates future hearing settings and sets the case to DISPOSED. Finished dispositions are routed automatically to ISP CHRI, the CCDOC release desk and the NICS firearm-flag queue.
          </div>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose} data-testid="dispose-cancel-btn">Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy || blocked} title={blocked ? "Every count must be disposed and every guilty count sentenced first" : undefined} data-testid="dispose-submit-btn">
            {blocked ? <><Lock className="w-3 h-3" /> Counts pending</> : "Enter Disposition"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
